import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API_URL, formatApiError } from '../../utils/api';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from '../ui/sheet';
import {
  Bell, BellRinging, Check, Trash, ArrowRight, Spinner,
  WarningCircle, Info as InfoIcon, CheckCircle, XCircle,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const POLL_INTERVAL = 30_000;

const SEVERITY_STYLES = {
  info:    { fg: '#0EA5E9', bg: '#E0F2FE', icon: InfoIcon },
  warning: { fg: '#A16207', bg: '#FEF3C7', icon: WarningCircle },
  success: { fg: '#047857', bg: '#E7F5EC', icon: CheckCircle },
  error:   { fg: '#B91C1C', bg: '#FBE7E7', icon: XCircle },
};

function timeAgo(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function NotificationsBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/notifications?limit=50`, { headers: authHeaders() });
      setItems(Array.isArray(data?.items) ? data.items : []);
      setUnread(Number(data?.unread || 0));
    } catch {
      /* silent; bell polls every 30s, no need to spam toasts */
    }
  }, []);

  // Initial fetch + interval poll
  useEffect(() => {
    fetchNotifications();
    const t = setInterval(fetchNotifications, POLL_INTERVAL);
    return () => clearInterval(t);
  }, [fetchNotifications]);

  // Refresh on drawer open
  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  const markRead = async (id) => {
    try {
      await axios.post(`${API_URL}/api/notifications/${id}/read`, null, { headers: authHeaders() });
      setItems((arr) => arr.map((n) => (n._id === id ? { ...n, read: true } : n)));
      setUnread((u) => Math.max(0, u - 1));
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to mark as read'));
    }
  };

  const markAllRead = async () => {
    try {
      setLoading(true);
      await axios.post(`${API_URL}/api/notifications/read-all`, null, { headers: authHeaders() });
      setItems((arr) => arr.map((n) => ({ ...n, read: true })));
      setUnread(0);
      toast.success('All notifications marked read');
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to mark all read'));
    } finally { setLoading(false); }
  };

  const dismiss = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/notifications/${id}`, { headers: authHeaders() });
      setItems((arr) => arr.filter((n) => n._id !== id));
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to dismiss'));
    }
  };

  const openItem = (n) => {
    if (!n.read) markRead(n._id);
    if (n.link) {
      setOpen(false);
      navigate(n.link);
    }
  };

  const Icon = unread > 0 ? BellRinging : Bell;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          data-testid="notifications-bell"
          className="relative inline-flex items-center justify-center h-10 w-10 rounded-full bg-white border border-[#EDE6D8] hover:bg-[#FCFAF6] transition shadow-[0_2px_8px_-3px_rgba(15,23,42,0.06)]"
          aria-label="Notifications"
        >
          <Icon size={18} weight={unread > 0 ? 'fill' : 'regular'} className="text-[#0A2540]" />
          {unread > 0 && (
            <span
              data-testid="notifications-unread-badge"
              className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#C9A35B] text-white text-[10px] font-bold flex items-center justify-center"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>
      </SheetTrigger>

      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-[#FCFAF6] border-l border-[#EDE6D8]"
        data-testid="notifications-drawer"
      >
        <SheetHeader className="text-left pb-4 border-b border-[#EDE6D8]">
          <div className="flex items-start justify-between gap-2">
            <div>
              <SheetTitle className="text-[#1C1917] text-xl" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Notifications
              </SheetTitle>
              <p className="text-[11px] text-[#78716C] mt-0.5">
                {unread > 0 ? `${unread} unread` : 'All caught up'} · refreshes every 30s
              </p>
            </div>
            {unread > 0 && (
              <button
                type="button"
                onClick={markAllRead}
                disabled={loading}
                data-testid="notifications-mark-all"
                className="text-[10px] uppercase tracking-wider font-semibold text-[#0A2540] hover:underline"
              >
                {loading ? <Spinner size={12} className="animate-spin inline" /> : 'Mark all read'}
              </button>
            )}
          </div>
        </SheetHeader>

        <div className="mt-4 space-y-2 overflow-y-auto max-h-[calc(100vh-160px)] pr-1">
          {items.length === 0 ? (
            <div className="text-center py-16 px-6">
              <Bell size={32} className="text-[#C9A35B] mx-auto mb-3" weight="duotone" />
              <p className="text-sm text-[#78716C]">No notifications yet.</p>
              <p className="text-[11px] text-[#A8A29E] mt-1">
                You&apos;ll see ops alerts here when cashier float runs low, a cashier goes inactive 24h+, a store stops trading, or your daily digest arrives.
              </p>
            </div>
          ) : items.map((n) => {
            const sev = SEVERITY_STYLES[n.severity] || SEVERITY_STYLES.info;
            const SevIcon = sev.icon;
            return (
              <div
                key={n._id}
                data-testid={`notification-item-${n._id}`}
                className={`group bg-white border rounded-xl p-3 transition cursor-pointer ${
                  n.read ? 'border-[#EDE6D8] opacity-70' : 'border-[#C9A35B]/40 shadow-[0_2px_8px_-4px_rgba(201,163,91,0.4)]'
                }`}
                onClick={() => openItem(n)}
              >
                <div className="flex items-start gap-3">
                  <div
                    className="h-7 w-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                    style={{ backgroundColor: sev.bg }}
                  >
                    <SevIcon size={14} weight="fill" style={{ color: sev.fg }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-semibold text-[#1C1917] leading-snug">{n.title}</p>
                      {!n.read && <span className="h-2 w-2 rounded-full bg-[#C9A35B] shrink-0 mt-1.5" />}
                    </div>
                    <p className="text-[12px] text-[#57534E] mt-1 leading-relaxed">{n.body}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-[10px] font-mono text-[#A8A29E]">{timeAgo(n.created_at)}</span>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                        {!n.read && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); markRead(n._id); }}
                            title="Mark read"
                            className="h-6 w-6 rounded-md hover:bg-[#FCFAF6] flex items-center justify-center"
                          >
                            <Check size={11} className="text-[#0A2540]" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); dismiss(n._id); }}
                          title="Dismiss"
                          className="h-6 w-6 rounded-md hover:bg-[#FBE7E7] flex items-center justify-center"
                        >
                          <Trash size={11} className="text-[#B91C1C]" />
                        </button>
                        {n.link && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); openItem(n); }}
                            title="Open"
                            className="h-6 w-6 rounded-md hover:bg-[#FCFAF6] flex items-center justify-center"
                          >
                            <ArrowRight size={11} className="text-[#0A2540]" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}
